import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet icon paths when bundled by webpack
import 'leaflet/dist/images/marker-icon-2x.png';
import 'leaflet/dist/images/marker-icon.png';
import 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({
    imagePath: '/assets/img/leaflet/',
    iconRetinaUrl: 'marker-icon-2x.png',
    iconUrl: 'marker-icon.png',
    shadowUrl: 'marker-shadow.png',
});

// -------------------------------
//  MAP CONFIG
// -------------------------------

const config = {
    scrollWheelZoom: false,
    request: {
        filter: {},
    },

    circles: [],
    pointlabels: [],
    polygons: [],
    polylines: [],
    rectangles: [],
    heatmaps: [],
};

// -------------------------------
//   BUILD MAP USING CONFIG
// -------------------------------

const initMap = async (mapContainerEl) => {
    if (!mapContainerEl || mapContainerEl.initialized) return;

    const mapEl = mapContainerEl.querySelector('[data-js-map]');

    if (!mapEl) return;

    const markersData = JSON.parse(mapEl.dataset.jsMap);

    mapContainerEl.initialized = true;

    // Apply width/height dynamically from config (with defaults)
    const mapWidth = config.map_width || '100';
    const mapWidthType = config.map_width_type || '%';
    const mapHeight = config.map_height || '690';
    const mapHeightType = config.map_height_type || 'px';
    mapEl.style.width = mapWidth + mapWidthType;
    mapEl.style.height = mapHeight + mapHeightType;

    // Extract Leaflet map options from config
    const mapOptions = {
        scrollWheelZoom: config.scrollWheelZoom ?? false,
    };

    // Initialize map with default center/zoom
    const defaultCenter = [51.0, 14.0];
    const defaultZoom = 10;

    const map = L.map(mapEl, mapOptions).setView(defaultCenter, defaultZoom);

    // Get zoom limits from config or use defaults
    const maxZoom = config.other_settings?.map_max_zoom
        ? parseInt(config.other_settings.map_max_zoom, 10)
        : 21;
    const minZoom = config.other_settings?.map_min_zoom
        ? parseInt(config.other_settings.map_min_zoom, 10)
        : 0;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: maxZoom,
        minZoom: minZoom,
        attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    // Create overlay element
    const overlay = document.createElement('div');
    overlay.className = 'map-overlay';
    overlay.innerHTML = `
        <div class="map-overlay__content">
            <button class="map-overlay__close" aria-label="Close overlay">&times;</button>
            <div class="map-overlay__body"></div>
        </div>
    `;
    mapContainerEl.appendChild(overlay);

    // Overlay functions
    const openOverlay = (markerData) => {
        const body = overlay.querySelector('.map-overlay__body');
        let content = '';

        if (markerData.title) {
            content += `<h3>${markerData.title}</h3>`;
        }

        if (markerData.address) {
            content += `<div class="map-overlay__address">${markerData.address}</div>`;
        }

        if (markerData.contact) {
          content += `<div class="map-overlay__info">${markerData.contact}</div>`;
        }

          if (markerData.openingHours) {
            content += `<div class="map-overlay__info">${markerData.openingHours}</div>`;
        }
        
         if (markerData.description) {
            content += `<div class="map-overlay__info">${markerData.description}</div>`;
        }

        if (markerData.link) {
            content += `<a href="${markerData.link}" class="btn btn-c-primary" target="_blank" rel="noopener noreferrer">Standortdetails</a>`;
        }

        body.innerHTML = content;
        overlay.classList.add('map-overlay--active');
    };

    const closeOverlay = () => {
        overlay.classList.remove('map-overlay--active');
    };

    // Close overlay handlers
    overlay
        .querySelector('.map-overlay__close')
        .addEventListener('click', closeOverlay);
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
            closeOverlay();
        }
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (
            e.key === 'Escape' &&
            overlay.classList.contains('map-overlay--active')
        ) {
            closeOverlay();
        }
    });

    // Add markers from API data
    const markerGroup = L.featureGroup();
    let hasValidMarkers = false;

    markersData.forEach((m) => {
        const markerLat = parseFloat(m.latitude);
        const markerLng = parseFloat(m.longitude);
        if (!isNaN(markerLat) && !isNaN(markerLng)) {
            const marker = L.marker([markerLat, markerLng]);
            marker.on('click', () => {
                openOverlay(m);
            });
            marker.addTo(map);
            markerGroup.addLayer(marker);
            hasValidMarkers = true;
        }
    });

    // Set map view based on config
    if (config.map_start_lat && config.map_start_lng) {
        // Use explicit start location
        const startLat = parseFloat(config.map_start_lat);
        const startLng = parseFloat(config.map_start_lng);
        const startZoom = parseInt(
            config.map_start_zoom || String(defaultZoom),
            10
        );
        map.setView([startLat, startLng], startZoom);
    } else if (hasValidMarkers) {
        const fitToMarkers = () => {
            if (markerGroup.getLayers().length === 1) {
                const latlng = markerGroup.getLayers()[0].getLatLng();
                map.setView(latlng, 13);
            } else {
                map.fitBounds(markerGroup.getBounds(), {
                    padding: [50, 80],
                });
            }
        };

        setTimeout(() => {
            map.invalidateSize();
            fitToMarkers();
        }, 200);

        // Refit on window resize with debounce
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                map.invalidateSize();
                fitToMarkers();
            }, 250);
        });
    }
};

// -------------------------------
//   CONSENT GATE
// -------------------------------

// The tiles come from tile.openstreetmap.org, so loading the map transmits the
// visitor's IP address to the OpenStreetMap Foundation. Cookiebot's auto
// blocking does not catch this (the requests are triggered at runtime by our
// own bundle), so the map is gated here: either the visitor has consented to
// the category below via Cookiebot, or they load a single map via the
// placeholder.
const CONSENT_CATEGORY = 'preferences';

const hasConsent = () => Boolean(window.Cookiebot?.consent?.[CONSENT_CATEGORY]);

const showMap = (mapContainerEl) => {
    mapContainerEl.querySelector('[data-js-map-consent]')?.remove();
    initMap(mapContainerEl);
};

const setupConsentGate = (mapContainerEl) => {
    if (mapContainerEl.initialized) return;

    const placeholderEl = mapContainerEl.querySelector('[data-js-map-consent]');

    // No placeholder means editmode, where Cookiebot is not loaded at all.
    if (!placeholderEl || hasConsent()) {
        showMap(mapContainerEl);
        return;
    }

    // Loading a single map is not persisted anywhere — the placeholder is back
    // on the next page view unless the visitor consents via Cookiebot.
    placeholderEl
        .querySelector('[data-js-map-consent-accept]')
        ?.addEventListener('click', () => showMap(mapContainerEl));
};

const mapContainerEls = document.querySelectorAll('.map-container');
mapContainerEls.forEach(setupConsentGate);

// Fired by Cookiebot once a choice has been submitted, and on every page view
// where a choice is already stored.
window.addEventListener('CookiebotOnAccept', () => {
    if (!hasConsent()) return;

    mapContainerEls.forEach(setupConsentGate);
});

if (window.Ext) {
    // editmode so map gets displayed at an instant, not just after reloading the page
    window.Ext.onReady(function () {
        const editMode = document.querySelector('.editmode');
        if (!editMode) return;

        const observer = new MutationObserver(() => {
            const mapEls = editMode.querySelectorAll('.map-container');

            mapEls.forEach(setupConsentGate);
        });

        observer.observe(editMode, {
            childList: true,
            subtree: true,
        });
    });
}
